/**
 * Endpoints de **Gestión → Clientes** (`/api/gestion/clientes/*`).
 *
 * El listado va paginado en el servidor (como la DataTable del panel anterior):
 * cada cambio de filtro, orden o página es una consulta nueva, y las claves de
 * caché reflejan esos filtros para no mezclar páginas.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  AccionPortal,
  AccionPortalRespuesta,
  ClienteFichaRespuesta,
  ClientesRespuesta,
  DescuentosPlacaRespuesta,
  DocumentoSunat,
  EdicionCliente,
  FiltrosClientes,
  NuevoCliente,
  PlacasClienteRespuesta,
  TipoPlaca,
} from './gestion.types';

const CLAVE = 'gestion';

export const clientesKeys = {
  listado: (filtros: FiltrosClientes) => [CLAVE, 'clientes', 'listado', filtros] as const,
  ficha: (id: number | null) => [CLAVE, 'clientes', 'ficha', id] as const,
  placas: (clienteId: number | null) => [CLAVE, 'clientes', 'placas', clienteId] as const,
  descuentos: (placaId: number | null) => [CLAVE, 'clientes', 'descuentos', placaId] as const,
  tiposPlaca: () => [CLAVE, 'clientes', 'tipos-placa'] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const clientesApi = {
  listado: (filtros: FiltrosClientes, signal?: AbortSignal): Promise<ClientesRespuesta> =>
    apiClient.get<ClientesRespuesta>(`/api/gestion/clientes${cadenaDe(filtros)}`, { signal }),

  ficha: (id: number, signal?: AbortSignal): Promise<ClienteFichaRespuesta> =>
    apiClient.get<ClienteFichaRespuesta>(`/api/gestion/clientes/${id}`, { signal }),

  consultaDocumento: (
    numero: string,
    tipo: 'dni' | 'ruc',
    signal?: AbortSignal,
  ): Promise<{ origen: 'local' | 'externo' | 'varios'; documento: DocumentoSunat }> =>
    apiClient.get<{ origen: 'local' | 'externo' | 'varios'; documento: DocumentoSunat }>(
      `/api/gestion/clientes/consulta-documento?numero=${encodeURIComponent(numero)}&tipo=${tipo}`,
      { signal },
    ),

  crear: (datos: NuevoCliente): Promise<{ cliente_id: number; mensaje: string }> =>
    apiClient.post<{ cliente_id: number; mensaje: string }>('/api/gestion/clientes', {
      body: datos,
    }),

  actualizar: (
    id: number,
    datos: EdicionCliente,
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/clientes/${id}`, { body: datos }),

  portal: (id: number, accion: AccionPortal): Promise<AccionPortalRespuesta> =>
    apiClient.post<AccionPortalRespuesta>(`/api/gestion/clientes/${id}/portal`, {
      body: { accion },
    }),

  eliminar: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/clientes/${id}`),

  placas: (clienteId: number, signal?: AbortSignal): Promise<PlacasClienteRespuesta> =>
    apiClient.get<PlacasClienteRespuesta>(`/api/gestion/clientes/${clienteId}/placas`, { signal }),

  crearPlaca: (
    clienteId: number,
    datos: { numero_placa: string; descripcion_placa?: string; cliente_tipo_placa_id?: number | null },
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>(`/api/gestion/clientes/${clienteId}/placas`, {
      body: datos,
    }),

  actualizarPlaca: (
    placaId: number,
    datos: { numero_placa: string; descripcion_placa?: string; cliente_tipo_placa_id?: number | null },
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.put<{ id: number; mensaje: string }>(`/api/gestion/clientes/placas/${placaId}`, {
      body: datos,
    }),

  eliminarPlaca: (placaId: number): Promise<{ id: number; mensaje: string }> =>
    apiClient.delete<{ id: number; mensaje: string }>(`/api/gestion/clientes/placas/${placaId}`),

  descuentosDePlaca: (placaId: number, signal?: AbortSignal): Promise<DescuentosPlacaRespuesta> =>
    apiClient.get<DescuentosPlacaRespuesta>(`/api/gestion/clientes/placas/${placaId}/descuentos`, {
      signal,
    }),

  guardarDescuentos: (
    placaId: number,
    descuentos: { producto_id: number; descuento: number; activo: boolean }[],
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/clientes/placas/${placaId}/descuentos`, {
      body: { descuentos },
    }),

  tiposPlaca: (signal?: AbortSignal): Promise<{ tipos: TipoPlaca[] }> =>
    apiClient.get<{ tipos: TipoPlaca[] }>('/api/gestion/clientes/tipos-placa', { signal }),

  crearTipoPlaca: (descripcion: string): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/clientes/tipos-placa', {
      body: { descripcion },
    }),

  actualizarTipoPlaca: (
    id: number,
    datos: { descripcion: string; estado?: number },
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.put<{ id: number; mensaje: string }>(`/api/gestion/clientes/tipos-placa/${id}`, {
      body: datos,
    }),

  aplicarDescuentoPorTipo: (
    tipoId: number,
    descuento: number,
  ): Promise<{ mensaje: string; descuentos_actualizados: number }> =>
    apiClient.post<{ mensaje: string; descuentos_actualizados: number }>(
      `/api/gestion/clientes/tipos-placa/${tipoId}/descuentos`,
      { body: { descuento } },
    ),

  aplicarDescuentosPorTipos: (
    descuentos: { tipo_placa_id: number; descuento: number }[],
  ): Promise<{ mensaje: string; descuentos_actualizados: number }> =>
    apiClient.post<{ mensaje: string; descuentos_actualizados: number }>(
      '/api/gestion/clientes/tipos-placa/descuentos',
      { body: { descuentos } },
    ),
};

/** Listado paginado de clientes. */
export function useClientes(filtros: FiltrosClientes) {
  return useQuery({
    queryKey: clientesKeys.listado(filtros),
    queryFn: ({ signal }) => clientesApi.listado(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Ficha del cliente con sus placas y lo que impide darlo de baja. */
export function useClienteFicha(id: number | null) {
  return useQuery({
    queryKey: clientesKeys.ficha(id),
    queryFn: ({ signal }) => clientesApi.ficha(id as number, signal),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

/** Refresca el listado y la ficha abierta. */
function refrescarClientes(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
}

/** Alta de cliente. */
export function useCrearCliente() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: clientesApi.crear,
    onSuccess: () => refrescarClientes(cliente),
  });
}

/** Edición de la ficha. */
export function useActualizarCliente() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: EdicionCliente }) =>
      clientesApi.actualizar(id, datos),
    onSuccess: () => refrescarClientes(cliente),
  });
}

/** Activar, desactivar o regenerar el portal del cliente. */
export function useAccionPortal() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, accion }: { id: number; accion: AccionPortal }) =>
      clientesApi.portal(id, accion),
    onSuccess: () => refrescarClientes(cliente),
  });
}

/** Dar de baja al cliente (sólo si no tiene comprobantes, vales ni créditos). */
export function useEliminarCliente() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: clientesApi.eliminar,
    onSuccess: () => refrescarClientes(cliente),
  });
}

/** Placas de un cliente con sus descuentos. */
export function usePlacasDeCliente(clienteId: number | null) {
  return useQuery({
    queryKey: clientesKeys.placas(clienteId),
    queryFn: ({ signal }) => clientesApi.placas(clienteId as number, signal),
    enabled: clienteId !== null,
  });
}

/** Productos con el descuento de una placa. */
export function useDescuentosDePlaca(placaId: number | null) {
  return useQuery({
    queryKey: clientesKeys.descuentos(placaId),
    queryFn: ({ signal }) => clientesApi.descuentosDePlaca(placaId as number, signal),
    enabled: placaId !== null,
  });
}

/** Tipos de placa. */
export function useTiposPlaca(activo = true) {
  return useQuery({
    queryKey: clientesKeys.tiposPlaca(),
    queryFn: ({ signal }) => clientesApi.tiposPlaca(signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Alta de una placa. */
export function useCrearPlaca(clienteId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      numero_placa: string;
      descripcion_placa?: string;
      cliente_tipo_placa_id?: number | null;
    }) => clientesApi.crearPlaca(clienteId as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Edición de una placa. */
export function useActualizarPlaca() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      id: number;
      numero_placa: string;
      descripcion_placa?: string;
      cliente_tipo_placa_id?: number | null;
    }) => clientesApi.actualizarPlaca(datos.id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Baja lógica de una placa. */
export function useEliminarPlaca() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (placaId: number) => clientesApi.eliminarPlaca(placaId),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Guarda los descuentos de una placa. */
export function useGuardarDescuentosPlaca(placaId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      descuentos: { producto_id: number; descuento: number; activo: boolean }[];
    }) => clientesApi.guardarDescuentos(placaId as number, datos.descuentos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Alta de un tipo de placa. */
export function useCrearTipoPlaca() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { descripcion: string }) => clientesApi.crearTipoPlaca(datos.descripcion),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Edición de un tipo de placa. */
export function useActualizarTipoPlaca() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { id: number; descripcion: string; estado?: number }) =>
      clientesApi.actualizarTipoPlaca(datos.id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Reparte un descuento a todas las placas de un tipo. */
export function useAplicarDescuentoPorTipo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { tipoId: number; descuento: number }) =>
      clientesApi.aplicarDescuentoPorTipo(datos.tipoId, datos.descuento),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Reparte el descuento de cada tipo a todas las placas. */
export function useAplicarDescuentosPorTipos() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { descuentos: { tipo_placa_id: number; descuento: number }[] }) =>
      clientesApi.aplicarDescuentosPorTipos(datos.descuentos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes'] });
    },
  });
}

/** Consulta el documento en SUNAT/RENIEC (o en la cartera local). */
export function useConsultaDocumento() {
  return useMutation({
    mutationFn: ({ numero, tipo }: { numero: string; tipo: 'dni' | 'ruc' }) =>
      clientesApi.consultaDocumento(numero, tipo),
  });
}
